<script lang="ts">
  import { onMount } from "svelte"
  import { slide } from "svelte/transition"
  import { xhr, toast, wait } from "$app/utils"
  import Button from "$app/components/ButtonSpinner.svelte"
  import LoadingSpinner from "$app/components/LoadingSpinner.svelte"
  import EditGoalStatus from "./EditGoalStatus.svelte"
  import {
    Newspaper,
    TriangleAlert,
    ChevronRight,
    ChevronDown,
    Lock
  } from "lucide-svelte"
  import Honeybadger from "$app/utils/honeybadger"
  import Tiptap from "$app/components/Tiptap.svelte"
  import { onlyTextButtons } from "$app/components/Tiptap/buttons"
  import { appStore } from "$app/store.svelte"

  const plan = appStore.plan
  const current_user: UserType = appStore.current_user

  type Props = {
    oncreate: (task: TaskType) => void
    parent_goal_id: number
    assignee_id: number
    end_of_quarter: string | Date
  }

  let { oncreate, parent_goal_id, assignee_id, end_of_quarter }: Props =
    $props()

  let isLoading = $state(false)

  let errors: Record<string, any> = $state({})
  let quarterlyGoals: GoalType[] = $state([])

  let departments: DepartmentType[] = $state([])
  let users: UserType[] = $state([])

  let show_description = $state(false)
  let show_leadership_notes = $state(false)
  let show_obstacles = $state(false)

  const initialForm = {
    name: "",
    expected_at: end_of_quarter,
    description: "",
    obstacles_text: "",
    leadership_notes: "",
    assignee_type: "Department",
    assignee_id: assignee_id,
    status: "on_track",
    goal_id: null,
    visibility: "plan"
  }

  let form = $state({ ...initialForm } as TaskType)

  async function initializeForm(): Promise<void> {
    isLoading = true

    try {
      let url = `/plans/${plan.id}/tasks/new.json`
      if (end_of_quarter !== null) {
        url = `${url}?end_of_quarter=${end_of_quarter}`
      }
      const resp: any = await xhr.get(url)

      if (end_of_quarter === null) {
        end_of_quarter = resp.data.end_of_quarter
      }

      form.expected_at = end_of_quarter
      departments = resp.data.departments
      users = resp.data.users
      quarterlyGoals = resp.data.quarterly_goals
      form.goal_id = parent_goal_id
    } catch (e: any) {
      if (typeof Honeybadger.notify !== "undefined") {
        Honeybadger.notify(e)
      }
      toast("There was an error loading the task form", "error")
    } finally {
      isLoading = false
      await wait(10)
      document.getElementById("task_name")?.focus()
    }
  }

  function updateStore(data: TaskType): void {
    try {
      data.expected_at = new Date(`${data.expected_at}T00:00:00`)
    } catch (e) {}
    appStore.tasks = [...appStore.tasks, data]
  }

  // Save New Task
  let saving = $state(false)

  async function createTask(): Promise<void> {
    saving = true

    try {
      const resp = await xhr.post<TaskType>(`/plans/${plan.id}/tasks.json`, {
        task: form
      })

      updateStore(resp.data)
      oncreate(resp.data)
    } catch (e: any) {
      toast("There was an error saving the new task", "error")
      if (e.response) {
        errors = { ...errors, ...e.response.data.errors }
      } else {
        Honeybadger.notify(e)
      }
    } finally {
      saving = false
    }
  }

  export function changesCount(): number {
    let count = 0
    Object.keys(initialForm).forEach((key) => {
      if (initialForm[key] !== form[key]) {
        count++
      }
    })

    return count
  }

  onMount(() => {
    form.goal_id = parent_goal_id
    initializeForm()
  })
</script>

<div class="mt-3 sm:mt-5 overflow-visible">
  <div class="flex w-full flex-col">
    <h3 class="text-2xl leading-6 font-medium text-gray-900">
      Create New Task
    </h3>
  </div>
  <div style="width:90vw"></div>
  <div class="mt-6">
    {#if isLoading}
      <LoadingSpinner />
    {:else}
      <div class="grid grid-cols-6 gap-y-3 gap-x-4">
        <div class="col-span-6">
          <p class="mb-1">Name</p>
          <div class="input-cont" class:error={errors.name}>
            <input
              type="text"
              id="task_name"
              bind:value={form.name}
              class="input"
            />
            <div class="msg">{errors.name}</div>
          </div>
        </div>

        <div class="col-span-6 sm:col-span-3">
          <label for="parent_goal_id" class="label">
            Parent Quarterly Goal
          </label>
          <div
            class="mt-1 input-cont parent-goal-cont"
            class:error={errors.goal}
          >
            <select id="parent_goal_id" bind:value={form.goal_id}>
              {#if quarterlyGoals.length}
                {#each departments as dept (dept.id)}
                  <optgroup label={dept.name}>
                    {#each quarterlyGoals.filter((g) => g.assignee_id === dept.id) as pg (pg.id)}
                      <option value={pg.id}>{pg.name}</option>
                    {/each}
                  </optgroup>
                {/each}
              {:else}
                <option value="">No Parent Goals for Expected Date</option>
              {/if}
            </select>
            <div class="msg">{errors.goal}</div>
          </div>
        </div>
        <div class="col-span-6 sm:col-span-3" class:error={errors.assignee_id}>
          <label for="task_assignee_id" class="label">Assignee</label>
          <div
            class="mt-1 input-cont assignee-cont"
            class:error={errors.assignee_id}
          >
            <select
              id="assignee_id"
              value={`${form.assignee_type}-${form.assignee_id}`}
              onchange={(e: Event) => {
                const [t, id] = (e.target as HTMLSelectElement).value.split("-")
                form.assignee_type = t
                form.assignee_id = +id
              }}
            >
              <optgroup label="Departments">
                {#each departments as dept (dept.id)}
                  <option value={`Department-${dept.id}`}>{dept.name}</option>
                {/each}
              </optgroup>
              <optgroup label="Members">
                {#each users as user (user.id)}
                  <option value={`User-${user.id}`}>{user.name}</option>
                {/each}
              </optgroup>
            </select>
            <div class="msg">{errors.assignee_id}</div>
          </div>
        </div>

        <div
          class="col-span-6 {appStore.editable
            ? 'sm:col-span-2'
            : 'sm:col-span-3'}"
        >
          <label for="task_status" class="label">Status</label>
          <div class="w-full">
            <EditGoalStatus
              id="task_status"
              type="Task"
              goalOrTask={form}
              update={false}
              onupdate={(t) => (form.status = t.status)}
            />
          </div>
        </div>
        <div
          class="relative expected-by-cont col-span-6 {appStore.editable
            ? 'sm:col-span-2'
            : 'sm:col-span-3'}"
        >
          <label for="expected_at" class="label">Expected By</label>
          <div class="input-cont" class:error={errors.expected_at}>
            <input type="date" class="input" bind:value={form.expected_at} />
            <div class="msg">{errors.expected_at}</div>
          </div>
        </div>
        {#if current_user.role === "leader" || current_user.role === "admin"}
          <div class="col-span-6 sm:col-span-2">
            <label for="task_visibility" class="label">Task Visibility</label>
            <div class="input-cont" class:error={errors.visibility}>
              <select id="task_visibility" bind:value={form.visibility}>
                <option value="plan">All Members</option>
                <option value="leadership">Only Leadership Team</option>
              </select>
              <div class="msg">{errors.visibility}</div>
            </div>
          </div>
        {/if}

        <div class="col-span-6 mt-2">
          <div class="flex items-center mb-1">
            <button
              id="toggle-description"
              class="flex items-center"
              onclick={() => (show_description = !show_description)}
            >
              {#if show_description}
                <ChevronDown class="text-gray-500 mr-2 size-5" />
              {:else}
                <ChevronRight class="text-gray-500 mr-2 size-5" />
              {/if}

              <Newspaper class="text-gray-400 mr-2 size-4" />
              <div class="text-sm text-gray-700">Description</div>
            </button>
          </div>
          {#if show_description}
            <div transition:slide class="pl-5 pt-1">
              <Tiptap
                placeholder="Enter a detailed description and/or action plan for this task"
                id="task_description"
                buttons={onlyTextButtons}
                onupdate={({ html }) => (form.description = html)}
              />
            </div>
          {/if}
        </div>

        {#if current_user.role === "leader" || current_user.role === "admin"}
          <div class="col-span-6">
            <div class="flex items-center mb-1">
              <button
                id="toggle-description"
                class="flex items-center"
                onclick={() => (show_leadership_notes = !show_leadership_notes)}
              >
                {#if show_leadership_notes}
                  <ChevronDown class="text-gray-500 mr-2 size-5" />
                {:else}
                  <ChevronRight class="text-gray-500 mr-2 size-5" />
                {/if}

                <Lock class="text-gray-400 mr-2 size-4" />
                <div class="text-sm text-gray-700">Leadership Team Notes</div>
              </button>
            </div>
            {#if show_leadership_notes}
              <div transition:slide class="pl-5 pt-1">
                <Tiptap
                  id="goal_leadership_notes"
                  buttons={onlyTextButtons}
                  placeholder="Enter any private notes here which are visible only for Leadership Team members."
                  onupdate={({ html }) => (form.leadership_notes = html)}
                />
              </div>
            {/if}
          </div>
        {/if}

        <div class="col-span-6">
          <div class="flex items-center mb-1 gap-2">
            <button
              id="toggle-obstacles"
              class="flex items-center"
              onclick={() => (show_obstacles = !show_obstacles)}
            >
              {#if show_obstacles}
                <ChevronDown class="text-gray-500" />
              {:else}
                <ChevronRight class="text-gray-500" />
              {/if}
              <TriangleAlert class="text-gray-400 size-4" />
              <div class="text-sm text-gray-700">Obstacles</div>
            </button>
          </div>
          {#if show_obstacles}
            <div transition:slide class="pl-5 pt-1">
              <Tiptap
                placeholder="Enter any obstacles to accomplishing this task"
                id="task_obstacles_text"
                buttons={onlyTextButtons}
                onupdate={({ html }) => (form.obstacles_text = html)}
              />
            </div>
          {/if}
        </div>
        <div class="col-span-6 py-3 text-right">
          <Button
            class="btn btn-rounded success btn-big"
            processing={saving}
            onclick={createTask}
          >
            Create Task
          </Button>
        </div>
      </div>
    {/if}
  </div>
</div>
